import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { Icon } from '@natera/material/lib/icon'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import { useNavigate } from 'react-router'
import { useConsignmentPDF, useCurrentConsignment } from '~/entities/consignment'
import { useAuditTrail } from '~/features/audit-trail'
import { useStepView } from '~/features/document-analytics'
import { cn } from '~/shared/lib/cn'
import { withForm } from '~/shared/lib/form'
import { pluralize } from '~/shared/lib/pluralize'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { useUndoRedo } from '~/shared/lib/use-undo-redo'
import { Alert, ScrollArea, Text } from '~/shared/ui'
import { ReviewActions } from '~/widgets/review-actions'
import { CONFIDENCE_SCORE_THRESHOLD, FORCE_DISABLE_AUDIT_TRAIL } from '../../config'
import type { DocumentPageRange } from '../../lib'
import {
  getDisplayDocumentId,
  hasNewDocumentPageRanges,
  moveDocumentPageRanges,
  splitDocumentPageRanges,
  unsplitDocumentPageRanges,
} from '../../lib'
import { useResizableContainer, useZoom } from '../../model'
import { LoadingSkeleton } from './loading-skeleton'
import { PageDialog } from './page-dialog'
import { SaveAndSplitDialog } from './save-and-split-dialog'
import { Separator } from './separator'
import { ZoomControls } from './zoom-controls'

type FormValues = { documentPageRanges: DocumentPageRange[] }

export const DocSplitting = withForm({
  defaultValues: {} as FormValues,
  props: {
    isUpdateError: false,
  },
  render: function Render({ form, isUpdateError }) {
    useStepView()
    const {
      data: { consignment, documents },
    } = useCurrentConsignment()
    const navigate = useNavigate()
    const { data: pdfUrl, isFetching: isPdfFetching } = useConsignmentPDF(consignment.key)
    const lastFetchedPdfUrl = useRef<string | null>(null)
    const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null)
    const { pageRefs, containerWidth, initPagesOnDocumentLoad } = useResizableContainer()
    const {
      zoomLevel,
      zoomIn,
      zoomOut,
      onFitToWidth,
      canZoomIn,
      canZoomOut,
      fitToWidth,
      setZoomLevel,
    } = useZoom()
    const [isSaveAndSplitDialogVisible, setIsSaveAndSplitDialogVisible] = useState(false)
    const [zoomedPageNumber, setZoomedPageNumber] = useState<number | null>(null)
    const { isAuditTrailVisible: isAuditTrailVisibleDefault } = useAuditTrail()
    const isAuditTrailVisible = FORCE_DISABLE_AUDIT_TRAIL ? false : isAuditTrailVisibleDefault

    useEffect(() => {
      if (pdfUrl && pdfUrl !== lastFetchedPdfUrl.current && !isPdfFetching) {
        lastFetchedPdfUrl.current = pdfUrl
        fetch(pdfUrl)
          .then((res) => res.arrayBuffer())
          .then((data) => {
            setPdfData(data)
          })
      }
    }, [pdfUrl, isPdfFetching])

    useEffect(() => {
      if (isAuditTrailVisible) {
        setZoomLevel((level) => (level === 4 ? 2 : level))
      } else {
        setZoomLevel((level) => (level === 2 ? 4 : level))
      }
    }, [isAuditTrailVisible, setZoomLevel])

    const {
      set: setHistoryValues,
      reset: resetHistoryValues,
      undo,
      redo,
      canUndo,
      canRedo,
    } = useUndoRedo({
      initialValues: form.options.defaultValues as FormValues,
      setFieldValue: form.setFieldValue,
    })

    const setDocumentPageRanges = (documentPageRanges: DocumentPageRange[]) => {
      setHistoryValues({ documentPageRanges })
      form.setFieldValue('documentPageRanges', documentPageRanges)
    }

    const handleSplitDocumentPageRangeClick = (documentId: string, splitIndex: number) => {
      const newDocumentPageRanges = splitDocumentPageRanges({
        documents,
        currentDocumentPageRanges: form.state.values.documentPageRanges,
        documentId,
        splitIndex,
      })
      setDocumentPageRanges(newDocumentPageRanges)
    }

    const handleUnsplitDocumentPageRangeClick = (documentId: string, unsplitIndex: number) => {
      const newDocumentPageRanges = unsplitDocumentPageRanges({
        documents,
        currentDocumentPageRanges: form.state.values.documentPageRanges,
        documentId,
        unsplitIndex,
      })
      setDocumentPageRanges(newDocumentPageRanges)
    }

    const handleDocumentPageRangeSeparatorDrop = (splitFromIndex: number, splitToIndex: number) => {
      const newDocumentPageRanges = moveDocumentPageRanges({
        documents,
        currentDocumentPageRanges: form.state.values.documentPageRanges,
        splitFromIndex,
        splitToIndex,
      })
      setDocumentPageRanges(newDocumentPageRanges)
    }

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event
      if (active.data.current && over?.data.current) {
        handleDocumentPageRangeSeparatorDrop(
          active.data.current.pageIndex,
          over.data.current.pageIndex,
        )
      }
    }

    const submit = (values: typeof form.state.values) => {
      if (hasNewDocumentPageRanges(values.documentPageRanges)) {
        setIsSaveAndSplitDialogVisible(true)
      } else {
        form.handleSubmit()
      }
    }

    const { isScrolled, sentinelElement } = useIsScrolled()

    return (
      <form
        className="relative flex max-w-full min-w-0 grow flex-col overflow-hidden"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <DndContext
          onDragEnd={handleDragEnd}
          modifiers={[restrictToFirstScrollableAncestor]}
        >
          <div className="group relative flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-8 pt-4">
                {form.state.values.documentPageRanges.length === 1 &&
                  form.state.values.documentPageRanges[0].pageRange.startPage ===
                    form.state.values.documentPageRanges[0].pageRange.endPage && (
                    <Alert
                      className="mb-6"
                      appearance="INFO"
                      materialIcon="info"
                    >
                      This asset only has one page, so nothing can be split
                    </Alert>
                  )}
                {isPdfFetching ? (
                  <LoadingSkeleton />
                ) : (
                  <Document
                    className="min-h-full"
                    file={pdfUrl}
                    onLoadSuccess={({ numPages }) => initPagesOnDocumentLoad(numPages)}
                    loading={<LoadingSkeleton />}
                  >
                    <form.Subscribe
                      selector={(state) => state.values}
                      children={(values) => {
                        let documentCounter = 0
                        return (
                          <div
                            className={cn('grid max-w-full gap-y-11 pb-6', {
                              'grid-cols-[1fr_auto]': zoomLevel === 1,
                              'grid-cols-[1fr_auto_1fr_auto]': zoomLevel === 2,
                              'grid-cols-[1fr_auto_1fr_auto_1fr_auto]': zoomLevel === 3,
                              'grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto]': zoomLevel === 4,
                            })}
                          >
                            {pageRefs.current.map((pageRef, index) => {
                              const { documentPageRanges } = values
                              const pageIndex = index
                              const currentDocumentPageRange = documentPageRanges.find(
                                (documentPageRange) =>
                                  documentPageRange.pageRange.startPage <= pageIndex &&
                                  documentPageRange.pageRange.endPage >= pageIndex,
                              )
                              if (!currentDocumentPageRange) {
                                return null
                              }
                              const isPageRangeStart =
                                currentDocumentPageRange.pageRange.startPage === pageIndex
                              if (isPageRangeStart) {
                                documentCounter++
                              }
                              const hasSeparator = !!documentPageRanges.find(
                                (documentPageRange) =>
                                  documentPageRange.pageRange.endPage === pageIndex &&
                                  documentPageRange.pageRange.endPage !==
                                    consignment.page_range.end_page,
                              )
                              const hasNextDocumentPageRange = documentPageRanges.find(
                                (documentPageRange) =>
                                  documentPageRange.pageRange.startPage > pageIndex,
                              )
                              const isLastPage =
                                pageIndex === currentDocumentPageRange.pageRange.endPage &&
                                !hasNextDocumentPageRange
                              const isLastPageInRow = (pageIndex + 1) % zoomLevel === 0
                              const shouldDocInfoBeWrapped =
                                zoomLevel === 4 && (hasSeparator || isLastPageInRow)
                              const pageCount =
                                currentDocumentPageRange.pageRange.endPage -
                                currentDocumentPageRange.pageRange.startPage +
                                1
                              return (
                                <Fragment key={pageIndex}>
                                  <div
                                    ref={pageRef}
                                    className={cn(
                                      'bg-surface-low relative flex min-w-0 flex-col items-center gap-1 p-4',
                                    )}
                                  >
                                    <div className="group/page relative">
                                      <Page
                                        pageNumber={pageIndex + 1}
                                        width={containerWidth}
                                        renderAnnotationLayer={false}
                                        renderTextLayer={false}
                                      />
                                      {zoomLevel !== 1 && (
                                        <button
                                          className="bg-content-medium-emphasis text-content-highest-emphasis-inverse absolute top-0 right-0 flex h-full w-full cursor-pointer items-center justify-center border-none opacity-0 transition-opacity select-none group-hover/page:opacity-100"
                                          onClick={() => setZoomedPageNumber(pageIndex + 1)}
                                        >
                                          <Icon
                                            className="text-[60px]"
                                            children="zoom_in"
                                          />
                                        </button>
                                      )}
                                    </div>
                                    <Text appearance="CAPTION">{pageIndex + 1}</Text>
                                    {isPageRangeStart && (
                                      <div
                                        className={cn(
                                          'absolute top-[calc(100%+2px)] left-0 flex items-center gap-x-1 gap-y-0.5',
                                          {
                                            // Width is added to cover the edge case when doc ID consists of wide characters
                                            'w-[calc(100%+16px)] flex-wrap': shouldDocInfoBeWrapped,
                                            'whitespace-nowrap': !shouldDocInfoBeWrapped,
                                          },
                                        )}
                                      >
                                        {currentDocumentPageRange.confidenceScore <
                                          CONFIDENCE_SCORE_THRESHOLD && (
                                          <Icon
                                            className="text-alert-warning-message -my-0.5 text-[24px]"
                                            children="help"
                                            fill
                                          />
                                        )}
                                        <Text appearance="SUBTITLE2">Doc {documentCounter}</Text>
                                        <Text
                                          className="uppercase"
                                          appearance="SUBTITLE2"
                                        >
                                          (
                                          {getDisplayDocumentId(
                                            currentDocumentPageRange.documentId,
                                          )}
                                          ,
                                        </Text>
                                        <Text appearance="SUBTITLE2">
                                          {pageCount} {pluralize(pageCount, 'page')})
                                        </Text>
                                      </div>
                                    )}
                                  </div>
                                  <Separator
                                    pageIndex={pageIndex}
                                    empty={isLastPage}
                                    dashed={!hasSeparator && !isLastPage}
                                    solid={hasSeparator && !isLastPage}
                                    onSplitClick={() => {
                                      handleSplitDocumentPageRangeClick(
                                        currentDocumentPageRange.documentId,
                                        pageIndex,
                                      )
                                    }}
                                    onUnsplitClick={() => {
                                      handleUnsplitDocumentPageRangeClick(
                                        currentDocumentPageRange.documentId,
                                        pageIndex,
                                      )
                                    }}
                                  />
                                </Fragment>
                              )
                            })}
                          </div>
                        )
                      }}
                    />
                  </Document>
                )}
              </div>
              {sentinelElement}
            </ScrollArea>

            <ZoomControls
              onZoomOut={zoomOut}
              onZoomIn={zoomIn}
              zoomOutDisabled={!canZoomOut}
              zoomInDisabled={!canZoomIn}
              fitToWidth={fitToWidth}
              onFitToWidth={onFitToWidth}
            />
          </div>
        </DndContext>

        <form.Subscribe
          selector={(state) => ({
            isDirty: state.isDirty,
            isSubmitting: state.isSubmitting,
            values: state.values,
          })}
          children={({ isDirty, isSubmitting, values }) => {
            return (
              <ReviewActions
                className="shrink-0 px-2"
                isDirty={isDirty}
                isSubmitDisabled={isSubmitting || !isDirty}
                onSubmit={() => submit(values)}
                submitLabel="Validate split"
                canUndo={canUndo}
                canRedo={canRedo}
                onRevert={() => {
                  form.reset()
                  resetHistoryValues()
                }}
                onUndo={undo}
                onRedo={redo}
                isError={isUpdateError}
                errorContent="opti couldn't complete the split validation due to a system issue. Please try again or refresh the page."
                isScrolled={isScrolled}
              />
            )
          }}
        />

        {isSaveAndSplitDialogVisible && (
          <form.Subscribe
            selector={(state) => ({
              values: state.values,
              isSubmitting: state.isSubmitting,
              isSubmitted: state.isSubmitted,
            })}
            children={({ values, isSubmitting, isSubmitted }) => (
              <SaveAndSplitDialog
                consignmentKey={consignment.key}
                documentPageRanges={values.documentPageRanges}
                onClose={() => {
                  setIsSaveAndSplitDialogVisible(false)
                  if (isSubmitted) {
                    navigate(0)
                  }
                }}
                onSubmit={form.handleSubmit}
                isSubmitting={isSubmitting}
                isSubmitted={isSubmitted}
              />
            )}
          />
        )}

        {zoomedPageNumber !== null && (
          <PageDialog
            // pdfData is a copy of the original pdfData, since it becomes "detached" when closing the dialog
            pdfData={pdfData ? pdfData.slice(0) : null}
            pageNumber={zoomedPageNumber}
            onClose={() => setZoomedPageNumber(null)}
          />
        )}
      </form>
    )
  },
})
